import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressTracker from '../../components/navigation/ProgressTracker';
import ContextualActionController from '../../components/navigation/ContextualActionController';
import QuestionCard from './components/QuestionCard';
import { useAssessment } from '../../components/navigation/AssessmentStateManager';
import { trackViewContentAPI } from '../../utils/conversionAPI';

const QuestionAssessment = () => {
  const navigate = useNavigate();
  const { state, answerQuestion, setScreen } = useAssessment();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(state?.currentQuestionIndex || 0);
  const [responses, setResponses] = useState(state?.responses || {});
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    setScreen('question-assessment');
    
    // Track assessment view with Conversion API
    trackViewContentAPI('Assessment Questions', 'Assessment');
  }, [setScreen]);

  const questions = [
    {
      id: 1,
      text: "I can't remember the last time I truly relaxed without feeling like I should be doing something more.",
      archetypes: { performer: 1 }
    },
    {
      id: 2,
      text: "I\'ve watched opportunities pass me by because I wasn\'t ready to move without certainty.",
      archetypes: { controller: 1 }
    },
    {
      id: 3,
      text: "I don't trust good seasons. Part of me is always bracing for it to fall apart.",
      archetypes: { destroyer: 1 }
    },
    {
      id: 4,
      text: "I scan the room before I speak—editing myself before anyone even pushes back.",
      archetypes: { ghost: 1 }
    },
    {
      id: 5,
      text: "I've said yes so automatically that I sometimes don't realize I didn't want to until it's too late.",
      archetypes: { pleaser: 1 }
    },
    {
      id: 6,
      text: "I've accomplished things most people never will—and I still don't feel like it's enough.",
      archetypes: { performer: 1 }
    },
    {
      id: 7,
      text: "I\'m so focused on what could go wrong that I rarely ask what could go right.",
      archetypes: { controller: 1 }
    },
    {
      id: 8,
      text: "Looking back, I can see a pattern: I pull away right when things start working.",
      archetypes: { destroyer: 1 }
    },
    {
      id: 9,
      text: "People assume I\'m fine because I never let them see otherwise.",
      archetypes: { ghost: 1 }
    },
    {
      id: 10,
      text: "I notice tension in a room before anyone else—and I feel responsible for fixing it.",
      archetypes: { pleaser: 1 }
    },
    {
      id: 11,
      text: "I know exactly what I produced today. I couldn\'t tell you how I actually feel.",
      archetypes: { performer: 1 }
    },
    {
      id: 12,
      text: "I carry more than I should because trusting others feels like a risk I can\'t afford.",
      archetypes: { controller: 1 }
    },
    {
      id: 13,
      text: "I know my escapes—the scrolling, the drinks, the busywork—and I use them anyway.",
      archetypes: { destroyer: 1 }
    },
    {
      id: 14,
      text: "If someone asked me what I actually want for myself—not my family, not my work—I'm not sure I'd have an answer.",
      archetypes: { ghost: 1 }
    },
    {
      id: 15,
      text: "I've stayed in jobs, relationships, and rooms longer than I should have—because leaving would've disappointed someone.",
      archetypes: { pleaser: 1 }
    },
    {
      id: 16,
      text: "One bad week can erase months of wins in my own head.",
      archetypes: { performer: 1 }
    },
    {
      id: 17,
      text: "My mind treats every small crack like it\'s about to become a collapse.",
      archetypes: { controller: 1 }
    },
    {
      id: 18,
      text: "There's a voice in me that says I don't deserve to keep what I've built.",
      archetypes: { destroyer: 1 }
    },
    {
      id: 19,
      text: "I\'ve swallowed opinions so many times that I\'m not always sure what I actually think anymore.",
      archetypes: { ghost: 1 }
    },
    {
      id: 20,
      text: "I apologize for things that aren\'t my fault just to keep the peace.",
      archetypes: { pleaser: 1 }
    },
    {
      id: 21,
      text: "When someone compliments me, my first instinct is to deflect or explain why it wasn't that impressive.",
      archetypes: { performer: 1 }
    },
    {
      id: 22,
      text: "I prepare for conversations, meetings, even vacations—because being caught off guard feels like failure.",
      archetypes: { controller: 1 }
    },
    {
      id: 23,
      text: "I\'ve started more things than I\'ve finished—and I\'ve stopped asking myself why.",
      archetypes: { destroyer: 1 }
    },
    {
      id: 24,
      text: "The people closest to me probably don't know what's really going on inside me.",
      archetypes: { ghost: 1 }
    },
    {
      id: 25,
      text: "Sometimes I catch myself mid-conversation and wonder: is this actually me, or the version of me I think they want?",
      archetypes: { pleaser: 1 }
    }
  ];

  const currentQuestion = questions?.[currentQuestionIndex];
  const totalQuestions = questions?.length;
  const hasAnswer = responses?.[currentQuestion?.id] !== undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentQuestionIndex]);

  const handleAnswerSelect = (answerValue) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion?.id]: answerValue
    }));

    // Create a mapping of all questions for score recalculation
    const allQuestionsMap = questions?.reduce((acc, q) => {
      acc[q.id] = { archetypes: q?.archetypes };
      return acc;
    }, {});

    // Store in global state with archetype points and all questions
    answerQuestion(currentQuestion?.id, answerValue, currentQuestion?.archetypes, allQuestionsMap);

    // Automatically advance to next question after selection
    setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        if (currentQuestionIndex < totalQuestions - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
        } else {
          navigate('/email-capture');
        }
        setIsTransitioning(false);
      }, 250);
    }, 600);
  };

  const handleNext = () => {
    if (!hasAnswer) return;

    setIsTransitioning(true);
    
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        navigate('/email-capture');
      }
      setIsTransitioning(false);
    }, 250);
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev - 1);
        setIsTransitioning(false);
      }, 250);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black">
      <ProgressTracker
        currentQuestion={currentQuestionIndex + 1}
        totalQuestions={totalQuestions}
        currentScreen="question-assessment"
      />

      <main className="pt-16 md:pt-20 pb-24 md:pb-28 px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className={`transition-opacity duration-250 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <QuestionCard
              questionNumber={currentQuestionIndex + 1}
              totalQuestions={totalQuestions}
              questionText={currentQuestion?.text}
              selectedAnswer={responses?.[currentQuestion?.id]}
              onAnswerSelect={handleAnswerSelect}
            />
          </div>
        </div>
      </main>

      <ContextualActionController
        currentScreen="question-assessment"
        isValid={hasAnswer}
        isLoading={isTransitioning}
        onAction={handleNext}
        showBackButton={currentQuestionIndex > 0}
        onBack={handleBack}
        secondaryText={`${currentQuestionIndex + 1} of ${totalQuestions} questions completed`}
      />
    </div>
  );
};

export default QuestionAssessment;